import { TAG_COLORS, type TagColorKey } from "./tagColors";

/**
 * Tags used as WhatsApp Web's lists: pills after the inbox views, a button in the conversation
 * header and a panel to manage them. The rules here are shared by those pieces and tested.
 */

type TagName = Record<string, string> | string;

const hex = (key: TagColorKey) => TAG_COLORS.find((c) => c.key === key)!.hex;

/** WhatsApp's own suggestions for a new list, in our palette. */
export const TAG_SUGGESTIONS = [
  { key: "tags.suggest.new_customer", color: hex("blue") },
  { key: "tags.suggest.new_order", color: hex("yellow") },
  { key: "tags.suggest.payment_pending", color: hex("orange") },
  { key: "tags.suggest.follow_up", color: hex("green") },
  { key: "tags.suggest.paid", color: hex("purple") },
  { key: "tags.suggest.order_done", color: hex("dark_gray") },
  { key: "tags.suggest.lead", color: hex("brown") },
  { key: "tags.suggest.important", color: hex("red") },
] as const;

/** Every text a tag name matches by, lower-cased: any of its locales. */
function variants(name: TagName): string[] {
  const all = typeof name === "string" ? [name] : Object.values(name);
  return all.map((v) => v.trim().toLowerCase()).filter(Boolean);
}

/** Whether another tag already has this name, in any language and any case. */
export function tagNameTaken(
  name: string,
  tags: Array<{ id: string; name: TagName }>,
  exceptId?: string,
): boolean {
  const wanted = name.trim().toLowerCase();
  if (!wanted) return false;
  return tags.some(
    (x) => x.id !== exceptId && variants(x.name).includes(wanted),
  );
}

/** The inbox pill id of a tag; views keep their own names. */
export const TAG_TAB = "tag:";

/** Active tags as inbox pills, after the views, as WhatsApp lists come after its filters. */
export function tagTabs(
  tags: Array<{ id: string; name: TagName; isActive: boolean }>,
  label: (name: TagName) => string,
): Array<{ id: string; label: string; group: "tags" }> {
  return tags
    .filter((x) => x.isActive)
    .map((x) => ({
      id: TAG_TAB + x.id,
      label: label(x.name),
      group: "tags" as const,
    }));
}

/** The tag a pill id stands for, or null for a view. */
export function tagOfTab(id: string): string | null {
  return id.startsWith(TAG_TAB) ? id.slice(TAG_TAB.length) : null;
}
